import veGeo from './data/ofacGeoVE.json';
import veCities from './data/ofacGeoVECities.json';
import usGeo from './data/ofacGeoUS.json';
import usPlaces from './data/ofacGeoUSPlaces.json';

export const OFAC_OTHER_VALUE = '__other__';

export type OfacLanguage = 'en' | 'es';
export type OfacNameStyle = 'hispanic' | 'portuguese' | 'western';
export type OfacAddressField =
  | 'country'
  | 'line'
  | 'line2'
  | 'neighborhood'
  | 'city'
  | 'region'
  | 'county'
  | 'postal';

export type OfacSelectOption = {
  value: string;
  label: string;
};

export type OfacNamedOption = OfacSelectOption & {
  labelEn: string;
  labelEs: string;
};

type VeGeoFile = {
  states: Array<{ code: string; name: string }>;
  counties: Record<string, Array<{ code: string; name: string }>>;
};

type UsGeoFile = {
  states: string[];
  counties: Record<string, string[]>;
};

type UsPlacesFile = Record<string, Record<string, string>>;

const VE_GEO = veGeo as VeGeoFile;
const VE_CITIES = veCities as Record<string, string[]>;
const US_GEO = usGeo as UsGeoFile;
const US_PLACES = usPlaces as UsPlacesFile;

export const OFAC_NAME_STYLES: Array<{
  value: OfacNameStyle;
  labelEn: string;
  labelEs: string;
  hintEn: string;
  hintEs: string;
}> = [
  {
    value: 'hispanic',
    labelEn: 'Spanish / Latin American',
    labelEs: 'Hispano / latinoamericano',
    hintEn: 'Given names, then father’s surname, then mother’s surname.',
    hintEs: 'Nombres, luego apellido del padre, luego apellido de la madre.',
  },
  {
    value: 'portuguese',
    labelEn: 'Portuguese / Brazilian',
    labelEs: 'Portugués / brasileño',
    hintEn: 'Given names, then mother’s surname, then father’s surname. Not the same as Spanish order.',
    hintEs: 'Nombres, luego apellido de la madre, luego apellido del padre. No es el mismo orden que el hispano.',
  },
  {
    value: 'western',
    labelEn: 'Western / English',
    labelEs: 'Occidental / inglés',
    hintEn: 'First, middle, last. Leave the second last name blank unless they use two family names.',
    hintEs: 'Nombre, segundo nombre, apellido. Deja el segundo apellido vacío salvo que usen dos apellidos.',
  },
];

export const OFAC_NAME_ORDER_HELP = {
  en: {
    overview:
      'Spanish/Latin American and Portuguese/Brazilian both use two family names, but they are not the same: the two surnames are written in reverse order. Western uses one family name.',
    hispanic:
      'Spanish / Latin American: given names, then paternal (father’s) surname, then maternal (mother’s) surname. Example: María Elena López García — López is the father, García the mother. SDN and most Spanish records file under the paternal surname.',
    portuguese:
      'Portuguese / Brazilian: given names, then maternal (mother’s) surname, then paternal (father’s) surname. The last surname is the primary one for filing. Example: José Carlos Silva Santos — Silva is the mother, Santos the father. Do not use Spanish order for a Brazilian legal name.',
    western:
      'Western / English: first name, optional middle name, last name. Put a hyphenated last name in Last name. Leave Second last name blank unless the person actually has two separate family names.',
    formChange:
      'Changing Name order relabels the boxes below and rebuilds the composed legal name. Spanish puts the father’s surname first; Portuguese puts the mother’s surname first. Those two are not the same.',
  },
  es: {
    overview:
      'Hispano/latinoamericano y portugués/brasileño usan dos apellidos, pero no son iguales: el orden de los apellidos está invertido. Occidental usa un apellido.',
    hispanic:
      'Hispano / latinoamericano: nombres, luego apellido paterno (padre), luego apellido materno (madre). Ejemplo: María Elena López García — López es el padre, García la madre. SDN y la mayoría de registros hispanos se archivan por el paterno.',
    portuguese:
      'Portugués / brasileño: nombres, luego apellido materno (madre), luego apellido paterno (padre). El último apellido es el principal para archivar. Ejemplo: José Carlos Silva Santos — Silva es la madre, Santos el padre. No uses el orden hispano para un nombre legal brasileño.',
    western:
      'Occidental / inglés: nombre, segundo nombre opcional, apellido. Un apellido con guion va en Apellido. Deja el segundo apellido vacío salvo que la persona tenga dos apellidos distintos.',
    formChange:
      'Cambiar Orden del nombre cambia las etiquetas de los campos y vuelve a armar el nombre legal. El hispano pone primero el apellido del padre; el portugués pone primero el de la madre. No son el mismo orden.',
  },
} as const;

export type OfacNamePartKey = 'givenName' | 'givenName2' | 'surname' | 'surname2';

export function ofacNameFieldsInDisplayOrder(
  style: OfacNameStyle | string | undefined,
): OfacNamePartKey[] {
  if (style === 'portuguese') {
    return ['givenName', 'givenName2', 'surname2', 'surname'];
  }
  if (style === 'western') {
    return ['givenName', 'givenName2', 'surname'];
  }
  return ['givenName', 'givenName2', 'surname', 'surname2'];
}

export function ofacNameOrderHelpItems(
  style: OfacNameStyle | string | undefined,
  language: OfacLanguage = 'en',
): Array<{ key: OfacNameStyle; title: string; body: string; selected: boolean }> {
  const selected = style === 'portuguese' || style === 'western' ? style : 'hispanic';
  const help = OFAC_NAME_ORDER_HELP[language];
  return OFAC_NAME_STYLES.map((row) => ({
    key: row.value,
    title: language === 'es' ? row.labelEs : row.labelEn,
    body: help[row.value],
    selected: row.value === selected,
  }));
}

export const OFAC_COUNTRIES: Array<{ code: string; nameEn: string; nameEs: string }> = [
  { code: 'US', nameEn: 'United States', nameEs: 'Estados Unidos' },
  { code: 'VE', nameEn: 'Venezuela', nameEs: 'Venezuela' },
  { code: 'MX', nameEn: 'Mexico', nameEs: 'México' },
  { code: 'CO', nameEn: 'Colombia', nameEs: 'Colombia' },
  { code: 'ES', nameEn: 'Spain', nameEs: 'España' },
  { code: 'AR', nameEn: 'Argentina', nameEs: 'Argentina' },
  { code: 'CL', nameEn: 'Chile', nameEs: 'Chile' },
  { code: 'PE', nameEn: 'Peru', nameEs: 'Perú' },
  { code: 'BR', nameEn: 'Brazil', nameEs: 'Brasil' },
  { code: 'CA', nameEn: 'Canada', nameEs: 'Canadá' },
  { code: 'GB', nameEn: 'United Kingdom', nameEs: 'Reino Unido' },
  { code: 'FR', nameEn: 'France', nameEs: 'Francia' },
  { code: 'DE', nameEn: 'Germany', nameEs: 'Alemania' },
  { code: 'IT', nameEn: 'Italy', nameEs: 'Italia' },
  { code: 'PT', nameEn: 'Portugal', nameEs: 'Portugal' },
  { code: 'NL', nameEn: 'Netherlands', nameEs: 'Países Bajos' },
  { code: 'BE', nameEn: 'Belgium', nameEs: 'Bélgica' },
  { code: 'CH', nameEn: 'Switzerland', nameEs: 'Suiza' },
  { code: 'AT', nameEn: 'Austria', nameEs: 'Austria' },
  { code: 'IE', nameEn: 'Ireland', nameEs: 'Irlanda' },
  { code: 'SE', nameEn: 'Sweden', nameEs: 'Suecia' },
  { code: 'NO', nameEn: 'Norway', nameEs: 'Noruega' },
  { code: 'DK', nameEn: 'Denmark', nameEs: 'Dinamarca' },
  { code: 'FI', nameEn: 'Finland', nameEs: 'Finlandia' },
  { code: 'PL', nameEn: 'Poland', nameEs: 'Polonia' },
  { code: 'CZ', nameEn: 'Czechia', nameEs: 'Chequia' },
  { code: 'HU', nameEn: 'Hungary', nameEs: 'Hungría' },
  { code: 'RO', nameEn: 'Romania', nameEs: 'Rumanía' },
  { code: 'GR', nameEn: 'Greece', nameEs: 'Grecia' },
  { code: 'BG', nameEn: 'Bulgaria', nameEs: 'Bulgaria' },
  { code: 'HR', nameEn: 'Croatia', nameEs: 'Croacia' },
  { code: 'SK', nameEn: 'Slovakia', nameEs: 'Eslovaquia' },
  { code: 'SI', nameEn: 'Slovenia', nameEs: 'Eslovenia' },
  { code: 'LT', nameEn: 'Lithuania', nameEs: 'Lituania' },
  { code: 'LV', nameEn: 'Latvia', nameEs: 'Letonia' },
  { code: 'EE', nameEn: 'Estonia', nameEs: 'Estonia' },
  { code: 'LU', nameEn: 'Luxembourg', nameEs: 'Luxemburgo' },
  { code: 'MT', nameEn: 'Malta', nameEs: 'Malta' },
  { code: 'TR', nameEn: 'Türkiye', nameEs: 'Turquía' },
  { code: 'RU', nameEn: 'Russia', nameEs: 'Rusia' },
  { code: 'UA', nameEn: 'Ukraine', nameEs: 'Ucrania' },
  { code: 'BY', nameEn: 'Belarus', nameEs: 'Bielorrusia' },
  { code: 'CN', nameEn: 'China', nameEs: 'China' },
  { code: 'JP', nameEn: 'Japan', nameEs: 'Japón' },
  { code: 'KR', nameEn: 'South Korea', nameEs: 'Corea del Sur' },
  { code: 'TW', nameEn: 'Taiwan', nameEs: 'Taiwán' },
  { code: 'HK', nameEn: 'Hong Kong', nameEs: 'Hong Kong' },
  { code: 'IN', nameEn: 'India', nameEs: 'India' },
  { code: 'PK', nameEn: 'Pakistan', nameEs: 'Pakistán' },
  { code: 'BD', nameEn: 'Bangladesh', nameEs: 'Bangladés' },
  { code: 'PH', nameEn: 'Philippines', nameEs: 'Filipinas' },
  { code: 'ID', nameEn: 'Indonesia', nameEs: 'Indonesia' },
  { code: 'TH', nameEn: 'Thailand', nameEs: 'Tailandia' },
  { code: 'VN', nameEn: 'Vietnam', nameEs: 'Vietnam' },
  { code: 'MY', nameEn: 'Malaysia', nameEs: 'Malasia' },
  { code: 'SG', nameEn: 'Singapore', nameEs: 'Singapur' },
  { code: 'AU', nameEn: 'Australia', nameEs: 'Australia' },
  { code: 'NZ', nameEn: 'New Zealand', nameEs: 'Nueva Zelanda' },
  { code: 'ZA', nameEn: 'South Africa', nameEs: 'Sudáfrica' },
  { code: 'EG', nameEn: 'Egypt', nameEs: 'Egipto' },
  { code: 'NG', nameEn: 'Nigeria', nameEs: 'Nigeria' },
  { code: 'KE', nameEn: 'Kenya', nameEs: 'Kenia' },
  { code: 'GH', nameEn: 'Ghana', nameEs: 'Ghana' },
  { code: 'MA', nameEn: 'Morocco', nameEs: 'Marruecos' },
  { code: 'IL', nameEn: 'Israel', nameEs: 'Israel' },
  { code: 'AE', nameEn: 'United Arab Emirates', nameEs: 'Emiratos Árabes Unidos' },
  { code: 'SA', nameEn: 'Saudi Arabia', nameEs: 'Arabia Saudita' },
  { code: 'QA', nameEn: 'Qatar', nameEs: 'Catar' },
  { code: 'KW', nameEn: 'Kuwait', nameEs: 'Kuwait' },
  { code: 'CU', nameEn: 'Cuba', nameEs: 'Cuba' },
  { code: 'DO', nameEn: 'Dominican Republic', nameEs: 'República Dominicana' },
  { code: 'PA', nameEn: 'Panama', nameEs: 'Panamá' },
  { code: 'CR', nameEn: 'Costa Rica', nameEs: 'Costa Rica' },
  { code: 'GT', nameEn: 'Guatemala', nameEs: 'Guatemala' },
  { code: 'HN', nameEn: 'Honduras', nameEs: 'Honduras' },
  { code: 'SV', nameEn: 'El Salvador', nameEs: 'El Salvador' },
  { code: 'NI', nameEn: 'Nicaragua', nameEs: 'Nicaragua' },
  { code: 'BO', nameEn: 'Bolivia', nameEs: 'Bolivia' },
  { code: 'EC', nameEn: 'Ecuador', nameEs: 'Ecuador' },
  { code: 'UY', nameEn: 'Uruguay', nameEs: 'Uruguay' },
  { code: 'PY', nameEn: 'Paraguay', nameEs: 'Paraguay' },
  { code: 'GY', nameEn: 'Guyana', nameEs: 'Guyana' },
  { code: 'HT', nameEn: 'Haiti', nameEs: 'Haití' },
  { code: 'JM', nameEn: 'Jamaica', nameEs: 'Jamaica' },
  { code: 'TT', nameEn: 'Trinidad and Tobago', nameEs: 'Trinidad y Tobago' },
  { code: 'PR', nameEn: 'Puerto Rico', nameEs: 'Puerto Rico' },
];

export const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  AS: 'American Samoa', GU: 'Guam', MP: 'Northern Mariana Islands', PR: 'Puerto Rico',
  VI: 'U.S. Virgin Islands', UM: 'U.S. Minor Outlying Islands',
};

type Subdivision = { code: string; name: string };

export const MX_STATES: Subdivision[] = [
  { code: 'AGU', name: 'Aguascalientes' }, { code: 'BCN', name: 'Baja California' },
  { code: 'BCS', name: 'Baja California Sur' }, { code: 'CAM', name: 'Campeche' },
  { code: 'CHP', name: 'Chiapas' }, { code: 'CHH', name: 'Chihuahua' },
  { code: 'CMX', name: 'Ciudad de México' }, { code: 'COA', name: 'Coahuila' },
  { code: 'COL', name: 'Colima' }, { code: 'DUR', name: 'Durango' },
  { code: 'GUA', name: 'Guanajuato' }, { code: 'GRO', name: 'Guerrero' },
  { code: 'HID', name: 'Hidalgo' }, { code: 'JAL', name: 'Jalisco' },
  { code: 'MEX', name: 'México' }, { code: 'MIC', name: 'Michoacán' },
  { code: 'MOR', name: 'Morelos' }, { code: 'NAY', name: 'Nayarit' },
  { code: 'NLE', name: 'Nuevo León' }, { code: 'OAX', name: 'Oaxaca' },
  { code: 'PUE', name: 'Puebla' }, { code: 'QUE', name: 'Querétaro' },
  { code: 'ROO', name: 'Quintana Roo' }, { code: 'SLP', name: 'San Luis Potosí' },
  { code: 'SIN', name: 'Sinaloa' }, { code: 'SON', name: 'Sonora' },
  { code: 'TAB', name: 'Tabasco' }, { code: 'TAM', name: 'Tamaulipas' },
  { code: 'TLA', name: 'Tlaxcala' }, { code: 'VER', name: 'Veracruz' },
  { code: 'YUC', name: 'Yucatán' }, { code: 'ZAC', name: 'Zacatecas' },
];

export const CO_DEPARTMENTS: Subdivision[] = [
  { code: 'AMA', name: 'Amazonas' }, { code: 'ANT', name: 'Antioquia' },
  { code: 'ARA', name: 'Arauca' }, { code: 'ATL', name: 'Atlántico' },
  { code: 'BOL', name: 'Bolívar' }, { code: 'BOY', name: 'Boyacá' },
  { code: 'CAL', name: 'Caldas' }, { code: 'CAQ', name: 'Caquetá' },
  { code: 'CAS', name: 'Casanare' }, { code: 'CAU', name: 'Cauca' },
  { code: 'CES', name: 'Cesar' }, { code: 'CHO', name: 'Chocó' },
  { code: 'COR', name: 'Córdoba' }, { code: 'CUN', name: 'Cundinamarca' },
  { code: 'DC', name: 'Bogotá, D.C.' }, { code: 'GUA', name: 'Guainía' },
  { code: 'GUV', name: 'Guaviare' }, { code: 'HUI', name: 'Huila' },
  { code: 'LAG', name: 'La Guajira' }, { code: 'MAG', name: 'Magdalena' },
  { code: 'MET', name: 'Meta' }, { code: 'NAR', name: 'Nariño' },
  { code: 'NSA', name: 'Norte de Santander' }, { code: 'PUT', name: 'Putumayo' },
  { code: 'QUI', name: 'Quindío' }, { code: 'RIS', name: 'Risaralda' },
  { code: 'SAP', name: 'San Andrés y Providencia' }, { code: 'SAN', name: 'Santander' },
  { code: 'SUC', name: 'Sucre' }, { code: 'TOL', name: 'Tolima' },
  { code: 'VAC', name: 'Valle del Cauca' }, { code: 'VAU', name: 'Vaupés' },
  { code: 'VID', name: 'Vichada' },
];

export const ES_REGIONS: Subdivision[] = [
  { code: 'AN', name: 'Andalucía' }, { code: 'AR', name: 'Aragón' },
  { code: 'AS', name: 'Asturias' }, { code: 'IB', name: 'Islas Baleares' },
  { code: 'CN', name: 'Canarias' }, { code: 'CB', name: 'Cantabria' },
  { code: 'CL', name: 'Castilla y León' }, { code: 'CM', name: 'Castilla-La Mancha' },
  { code: 'CT', name: 'Cataluña' }, { code: 'EX', name: 'Extremadura' },
  { code: 'GA', name: 'Galicia' }, { code: 'MD', name: 'Madrid' },
  { code: 'MC', name: 'Murcia' }, { code: 'NC', name: 'Navarra' },
  { code: 'PV', name: 'País Vasco' }, { code: 'RI', name: 'La Rioja' },
  { code: 'VC', name: 'Comunitat Valenciana' }, { code: 'CE', name: 'Ceuta' },
  { code: 'ML', name: 'Melilla' },
];

export const AR_PROVINCES: Subdivision[] = [
  { code: 'C', name: 'Ciudad Autónoma de Buenos Aires' },
  { code: 'B', name: 'Buenos Aires' }, { code: 'K', name: 'Catamarca' },
  { code: 'H', name: 'Chaco' }, { code: 'U', name: 'Chubut' },
  { code: 'X', name: 'Córdoba' }, { code: 'W', name: 'Corrientes' },
  { code: 'E', name: 'Entre Ríos' }, { code: 'P', name: 'Formosa' },
  { code: 'Y', name: 'Jujuy' }, { code: 'L', name: 'La Pampa' },
  { code: 'F', name: 'La Rioja' }, { code: 'M', name: 'Mendoza' },
  { code: 'N', name: 'Misiones' }, { code: 'Q', name: 'Neuquén' },
  { code: 'R', name: 'Río Negro' }, { code: 'A', name: 'Salta' },
  { code: 'J', name: 'San Juan' }, { code: 'D', name: 'San Luis' },
  { code: 'Z', name: 'Santa Cruz' }, { code: 'S', name: 'Santa Fe' },
  { code: 'G', name: 'Santiago del Estero' },
  { code: 'V', name: 'Tierra del Fuego' }, { code: 'T', name: 'Tucumán' },
];

export const CL_REGIONS: Subdivision[] = [
  { code: 'AP', name: 'Arica y Parinacota' }, { code: 'TA', name: 'Tarapacá' },
  { code: 'AN', name: 'Antofagasta' }, { code: 'AT', name: 'Atacama' },
  { code: 'CO', name: 'Coquimbo' }, { code: 'VS', name: 'Valparaíso' },
  { code: 'RM', name: 'Metropolitana de Santiago' }, { code: 'LI', name: "O'Higgins" },
  { code: 'ML', name: 'Maule' }, { code: 'NB', name: 'Ñuble' },
  { code: 'BI', name: 'Biobío' }, { code: 'AR', name: 'La Araucanía' },
  { code: 'LR', name: 'Los Ríos' }, { code: 'LL', name: 'Los Lagos' },
  { code: 'AI', name: 'Aysén' }, { code: 'MA', name: 'Magallanes' },
];

export const PE_DEPARTMENTS: Subdivision[] = [
  { code: 'AMA', name: 'Amazonas' }, { code: 'ANC', name: 'Áncash' },
  { code: 'APU', name: 'Apurímac' }, { code: 'ARE', name: 'Arequipa' },
  { code: 'AYA', name: 'Ayacucho' }, { code: 'CAJ', name: 'Cajamarca' },
  { code: 'CAL', name: 'Callao' }, { code: 'CUS', name: 'Cusco' },
  { code: 'HUV', name: 'Huancavelica' }, { code: 'HUC', name: 'Huánuco' },
  { code: 'ICA', name: 'Ica' }, { code: 'JUN', name: 'Junín' },
  { code: 'LAL', name: 'La Libertad' }, { code: 'LAM', name: 'Lambayeque' },
  { code: 'LIM', name: 'Lima' }, { code: 'LOR', name: 'Loreto' },
  { code: 'MDD', name: 'Madre de Dios' }, { code: 'MOQ', name: 'Moquegua' },
  { code: 'PAS', name: 'Pasco' }, { code: 'PIU', name: 'Piura' },
  { code: 'PUN', name: 'Puno' }, { code: 'SAM', name: 'San Martín' },
  { code: 'TAC', name: 'Tacna' }, { code: 'TUM', name: 'Tumbes' },
  { code: 'UCA', name: 'Ucayali' },
];

export const BR_STATES: Subdivision[] = [
  { code: 'AC', name: 'Acre' }, { code: 'AL', name: 'Alagoas' },
  { code: 'AP', name: 'Amapá' }, { code: 'AM', name: 'Amazonas' },
  { code: 'BA', name: 'Bahia' }, { code: 'CE', name: 'Ceará' },
  { code: 'DF', name: 'Distrito Federal' }, { code: 'ES', name: 'Espírito Santo' },
  { code: 'GO', name: 'Goiás' }, { code: 'MA', name: 'Maranhão' },
  { code: 'MT', name: 'Mato Grosso' }, { code: 'MS', name: 'Mato Grosso do Sul' },
  { code: 'MG', name: 'Minas Gerais' }, { code: 'PA', name: 'Pará' },
  { code: 'PB', name: 'Paraíba' }, { code: 'PR', name: 'Paraná' },
  { code: 'PE', name: 'Pernambuco' }, { code: 'PI', name: 'Piauí' },
  { code: 'RJ', name: 'Rio de Janeiro' }, { code: 'RN', name: 'Rio Grande do Norte' },
  { code: 'RS', name: 'Rio Grande do Sul' }, { code: 'RO', name: 'Rondônia' },
  { code: 'RR', name: 'Roraima' }, { code: 'SC', name: 'Santa Catarina' },
  { code: 'SP', name: 'São Paulo' }, { code: 'SE', name: 'Sergipe' },
  { code: 'TO', name: 'Tocantins' },
];

export const CA_PROVINCES: Subdivision[] = [
  { code: 'AB', name: 'Alberta' }, { code: 'BC', name: 'British Columbia' },
  { code: 'MB', name: 'Manitoba' }, { code: 'NB', name: 'New Brunswick' },
  { code: 'NL', name: 'Newfoundland and Labrador' }, { code: 'NS', name: 'Nova Scotia' },
  { code: 'NT', name: 'Northwest Territories' }, { code: 'NU', name: 'Nunavut' },
  { code: 'ON', name: 'Ontario' }, { code: 'PE', name: 'Prince Edward Island' },
  { code: 'QC', name: 'Quebec' }, { code: 'SK', name: 'Saskatchewan' },
  { code: 'YT', name: 'Yukon' },
];

const HARDCODED_STATES: Record<string, Subdivision[]> = {
  MX: MX_STATES,
  CO: CO_DEPARTMENTS,
  ES: ES_REGIONS,
  AR: AR_PROVINCES,
  CL: CL_REGIONS,
  PE: PE_DEPARTMENTS,
  BR: BR_STATES,
  CA: CA_PROVINCES,
};

export const MAJOR_CITIES: Record<string, Array<{ name: string; state: string; county?: string }>> = {
  VE: [
    { name: 'Caracas', state: 'VE01', county: 'VE0101' },
    { name: 'Los Teques', state: 'VE15', county: 'VE1510' },
    { name: 'Petare', state: 'VE15', county: 'VE1519' },
    { name: 'Baruta', state: 'VE15', county: 'VE1503' },
    { name: 'Guarenas', state: 'VE15', county: 'VE1517' },
    { name: 'Valencia', state: 'VE08', county: 'VE0814' },
    { name: 'Puerto Cabello', state: 'VE08', county: 'VE0811' },
    { name: 'Maracaibo', state: 'VE23', county: 'VE2313' },
    { name: 'Barquisimeto', state: 'VE13', county: 'VE1303' },
    { name: 'Maracay', state: 'VE05', county: 'VE0503' },
    { name: 'Ciudad Guayana', state: 'VE07', county: 'VE0701' },
    { name: 'Maturín', state: 'VE16', county: 'VE1608' },
    { name: 'Barcelona', state: 'VE03', county: 'VE0318' },
    { name: 'Puerto La Cruz', state: 'VE03', county: 'VE0308' },
    { name: 'Mérida', state: 'VE14', county: 'VE1412' },
    { name: 'San Cristóbal', state: 'VE20', county: 'VE2023' },
    { name: 'Ciudad Bolívar', state: 'VE07', county: 'VE0705' },
  ],
  US: [
    { name: 'Houston', state: 'TX', county: 'Harris' },
    { name: 'Pasadena', state: 'TX', county: 'Harris' },
    { name: 'Baytown', state: 'TX', county: 'Harris' },
    { name: 'Bellaire', state: 'TX', county: 'Harris' },
    { name: 'San Antonio', state: 'TX', county: 'Bexar' },
    { name: 'Dallas', state: 'TX', county: 'Dallas' },
    { name: 'Austin', state: 'TX', county: 'Travis' },
    { name: 'Fort Worth', state: 'TX', county: 'Tarrant' },
    { name: 'El Paso', state: 'TX', county: 'El Paso' },
    { name: 'Los Angeles', state: 'CA', county: 'Los Angeles' },
    { name: 'Long Beach', state: 'CA', county: 'Los Angeles' },
    { name: 'San Diego', state: 'CA', county: 'San Diego' },
    { name: 'San Francisco', state: 'CA', county: 'San Francisco' },
    { name: 'San Jose', state: 'CA', county: 'Santa Clara' },
    { name: 'New York', state: 'NY', county: 'New York' },
    { name: 'Brooklyn', state: 'NY', county: 'Kings' },
    { name: 'Miami', state: 'FL', county: 'Miami-Dade' },
    { name: 'Chicago', state: 'IL', county: 'Cook' },
    { name: 'Phoenix', state: 'AZ', county: 'Maricopa' },
    { name: 'Seattle', state: 'WA', county: 'King' },
    { name: 'Denver', state: 'CO', county: 'Denver' },
    { name: 'Atlanta', state: 'GA', county: 'Fulton' },
    { name: 'Boston', state: 'MA', county: 'Suffolk' },
    { name: 'Washington', state: 'DC', county: 'District of Columbia' },
  ],
  MX: [
    { name: 'Ciudad de México', state: 'CMX' },
    { name: 'Guadalajara', state: 'JAL' },
    { name: 'Zapopan', state: 'JAL' },
    { name: 'Monterrey', state: 'NLE' },
    { name: 'Tijuana', state: 'BCN' },
    { name: 'Puebla', state: 'PUE' },
    { name: 'León', state: 'GUA' },
    { name: 'Juárez', state: 'CHH' },
    { name: 'Mérida', state: 'YUC' },
    { name: 'Cancún', state: 'ROO' },
  ],
  CO: [
    { name: 'Bogotá', state: 'DC' },
    { name: 'Medellín', state: 'ANT' },
    { name: 'Cali', state: 'VAC' },
    { name: 'Barranquilla', state: 'ATL' },
    { name: 'Cartagena', state: 'BOL' },
    { name: 'Bucaramanga', state: 'SAN' },
    { name: 'Cúcuta', state: 'NSA' },
    { name: 'Pereira', state: 'RIS' },
  ],
  ES: [
    { name: 'Madrid', state: 'MD' },
    { name: 'Barcelona', state: 'CT' },
    { name: 'Valencia', state: 'VC' },
    { name: 'Sevilla', state: 'AN' },
    { name: 'Málaga', state: 'AN' },
    { name: 'Bilbao', state: 'PV' },
    { name: 'Zaragoza', state: 'AR' },
    { name: 'Murcia', state: 'MC' },
    { name: 'Palma', state: 'IB' },
  ],
  AR: [
    { name: 'Buenos Aires', state: 'C' },
    { name: 'La Plata', state: 'B' },
    { name: 'Mar del Plata', state: 'B' },
    { name: 'Córdoba', state: 'X' },
    { name: 'Rosario', state: 'S' },
    { name: 'Mendoza', state: 'M' },
    { name: 'Salta', state: 'A' },
    { name: 'San Miguel de Tucumán', state: 'T' },
  ],
  CL: [
    { name: 'Santiago', state: 'RM' },
    { name: 'Valparaíso', state: 'VS' },
    { name: 'Viña del Mar', state: 'VS' },
    { name: 'Concepción', state: 'BI' },
    { name: 'Antofagasta', state: 'AN' },
    { name: 'Temuco', state: 'AR' },
  ],
  PE: [
    { name: 'Lima', state: 'LIM' },
    { name: 'Callao', state: 'CAL' },
    { name: 'Arequipa', state: 'ARE' },
    { name: 'Trujillo', state: 'LAL' },
    { name: 'Chiclayo', state: 'LAM' },
    { name: 'Piura', state: 'PIU' },
    { name: 'Cusco', state: 'CUS' },
  ],
  BR: [
    { name: 'São Paulo', state: 'SP' },
    { name: 'Rio de Janeiro', state: 'RJ' },
    { name: 'Brasília', state: 'DF' },
    { name: 'Belo Horizonte', state: 'MG' },
    { name: 'Salvador', state: 'BA' },
    { name: 'Fortaleza', state: 'CE' },
    { name: 'Curitiba', state: 'PR' },
    { name: 'Recife', state: 'PE' },
    { name: 'Porto Alegre', state: 'RS' },
    { name: 'Manaus', state: 'AM' },
  ],
  CA: [
    { name: 'Toronto', state: 'ON' },
    { name: 'Ottawa', state: 'ON' },
    { name: 'Montreal', state: 'QC' },
    { name: 'Vancouver', state: 'BC' },
    { name: 'Calgary', state: 'AB' },
    { name: 'Edmonton', state: 'AB' },
  ],
};

export const OFAC_CITY_HELP = {
  en: {
    ve:
      'City lists the municipal seat and nearby towns. For someone living in Caracas: Venezuela → Distrito Capital → Libertador → Caracas. Eastern/metro Caracas (Petare, Baruta, Chacao, El Hatillo) is Miranda plus that municipality; Caracas is also listed there. Miranda → Simón Bolívar is Yare (Valles del Tuy), not Caracas.',
    generic:
      'Pick a listed city when you can. Use Other (type it) for a locality that is not in the catalog.',
  },
  es: {
    ve:
      'Ciudad muestra la cabecera municipal y pueblos cercanos. Para alguien que vive en Caracas: Venezuela → Distrito Capital → Libertador → Caracas. El este/área metropolitana (Petare, Baruta, Chacao, El Hatillo) es Miranda más ese municipio; Caracas también aparece ahí. Miranda → Simón Bolívar es Yare (Valles del Tuy), no Caracas.',
    generic:
      'Elige una ciudad de la lista cuando puedas. Usa Otro (escribir) para una localidad que no esté en el catálogo.',
  },
};

export function ofacCityHelp(country: string, language: OfacLanguage = 'en'): string {
  const code = String(country || '').trim().toUpperCase();
  return code === 'VE' ? OFAC_CITY_HELP[language].ve : OFAC_CITY_HELP[language].generic;
}

type NationalIdType = {
  value: string;
  labelEn: string;
  labelEs: string;
};

const NATIONAL_ID_TYPES: Record<string, NationalIdType[]> = {
  VE: [
    { value: 'cedula', labelEn: 'Cédula', labelEs: 'Cédula' },
    { value: 'rif', labelEn: 'RIF', labelEs: 'RIF' },
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
    { value: 'other', labelEn: 'Other', labelEs: 'Otro' },
  ],
  US: [
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
    { value: 'drivers_license', labelEn: 'Driver license', labelEs: 'Licencia de conducir' },
    { value: 'itin', labelEn: 'ITIN', labelEs: 'ITIN' },
    { value: 'other', labelEn: 'Other', labelEs: 'Otro' },
  ],
  MX: [
    { value: 'curp', labelEn: 'CURP', labelEs: 'CURP' },
    { value: 'rfc', labelEn: 'RFC', labelEs: 'RFC' },
    { value: 'ine', labelEn: 'INE', labelEs: 'INE' },
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
  ],
  CO: [
    { value: 'cedula', labelEn: 'Cédula', labelEs: 'Cédula' },
    { value: 'nit', labelEn: 'NIT', labelEs: 'NIT' },
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
  ],
  ES: [
    { value: 'dni', labelEn: 'DNI', labelEs: 'DNI' },
    { value: 'nie', labelEn: 'NIE', labelEs: 'NIE' },
    { value: 'nif', labelEn: 'NIF', labelEs: 'NIF' },
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
  ],
  BR: [
    { value: 'cpf', labelEn: 'CPF', labelEs: 'CPF' },
    { value: 'rg', labelEn: 'RG', labelEs: 'RG' },
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
  ],
  AR: [
    { value: 'dni', labelEn: 'DNI', labelEs: 'DNI' },
    { value: 'cuit', labelEn: 'CUIT', labelEs: 'CUIT' },
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
  ],
  CL: [
    { value: 'rut', labelEn: 'RUT', labelEs: 'RUT' },
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
  ],
  PE: [
    { value: 'dni', labelEn: 'DNI', labelEs: 'DNI' },
    { value: 'ruc', labelEn: 'RUC', labelEs: 'RUC' },
    { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
  ],
};

const GENERIC_NATIONAL_ID_TYPES: NationalIdType[] = [
  { value: 'national_id', labelEn: 'National ID', labelEs: 'ID nacional' },
  { value: 'tax_id', labelEn: 'Tax ID', labelEs: 'ID fiscal' },
  { value: 'passport', labelEn: 'Passport', labelEs: 'Pasaporte' },
  { value: 'other', labelEn: 'Other', labelEs: 'Otro' },
];

const ADDRESS_LAYOUTS: Record<string, OfacAddressField[]> = {
  US: ['country', 'line', 'line2', 'city', 'region', 'county', 'postal'],
  VE: ['country', 'line', 'neighborhood', 'region', 'county', 'city', 'postal'],
  MX: ['country', 'line', 'neighborhood', 'county', 'region', 'city', 'postal'],
  GB: ['country', 'line', 'line2', 'city', 'postal'],
};

const GENERIC_ADDRESS_LAYOUT: OfacAddressField[] = [
  'country', 'line', 'line2', 'city', 'region', 'county', 'postal',
];

const PINNED_COUNTRY_CODES = ['US', 'VE', 'MX'];

function countryName(row: { nameEn: string; nameEs: string }, language: OfacLanguage): string {
  return language === 'es' ? row.nameEs : row.nameEn;
}

export function ofacCountryOptions(language: OfacLanguage = 'en'): OfacSelectOption[] {
  const pinned = PINNED_COUNTRY_CODES
    .map((code) => OFAC_COUNTRIES.find((row) => row.code === code))
    .filter((row): row is (typeof OFAC_COUNTRIES)[number] => Boolean(row));
  const rest = OFAC_COUNTRIES
    .filter((row) => !PINNED_COUNTRY_CODES.includes(row.code))
    .slice()
    .sort((left, right) => countryName(left, language).localeCompare(countryName(right, language), language));
  const seen = new Set<string>();
  return [...pinned, ...rest]
    .filter((row) => {
      if (seen.has(row.code)) {
        return false;
      }
      seen.add(row.code);
      return true;
    })
    .map((row) => ({ value: row.code, label: countryName(row, language) }));
}

export function ofacCountryLabel(code: string, language: OfacLanguage = 'en'): string {
  const normalized = String(code || '').trim();
  if (!normalized || normalized === OFAC_OTHER_VALUE) {
    return '';
  }
  const match = OFAC_COUNTRIES.find((row) => row.code === normalized);
  return match ? countryName(match, language) : normalized;
}

function asOptions(rows: Subdivision[]): OfacSelectOption[] {
  return rows.map((row) => ({ value: row.code, label: row.name }));
}

export function ofacStateOptions(country: string): OfacSelectOption[] {
  const code = String(country || '').trim().toUpperCase();
  if (code === 'VE') {
    return VE_GEO.states.map((row) => ({ value: row.code, label: row.name }));
  }
  if (code === 'US') {
    return US_GEO.states.map((state) => ({
      value: state,
      label: US_STATE_NAMES[state] || state,
    }));
  }
  return asOptions(HARDCODED_STATES[code] || []);
}

export function ofacCountyOptions(country: string, state: string): OfacSelectOption[] {
  const countryCode = String(country || '').trim().toUpperCase();
  const stateCode = String(state || '').trim();
  if (!stateCode || stateCode === OFAC_OTHER_VALUE) {
    return [];
  }
  if (countryCode === 'VE') {
    return (VE_GEO.counties[stateCode] || []).map((row) => ({ value: row.code, label: row.name }));
  }
  if (countryCode === 'US') {
    return (US_GEO.counties[stateCode] || []).map((name) => ({ value: name, label: name }));
  }
  return [];
}

function uniqueCityOptions(names: string[]): OfacSelectOption[] {
  const seen = new Set<string>();
  const out: OfacSelectOption[] = [];
  for (const name of names) {
    const trimmed = String(name || '').trim();
    if (!trimmed) {
      continue;
    }
    const key = trimmed.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push({ value: trimmed, label: trimmed });
  }
  return out;
}

function veCityNames(stateCode: string, countyCode: string): string[] {
  if (countyCode && countyCode !== OFAC_OTHER_VALUE) {
    return VE_CITIES[countyCode] || [];
  }
  if (stateCode && stateCode !== OFAC_OTHER_VALUE) {
    const names: string[] = [];
    for (const row of VE_GEO.counties[stateCode] || []) {
      names.push(...(VE_CITIES[row.code] || []));
    }
    return names;
  }
  return [];
}

export function ofacCityOptions(country: string, state?: string, county?: string): OfacSelectOption[] {
  const countryCode = String(country || '').trim().toUpperCase();
  const stateCode = String(state || '').trim();
  const countyCode = String(county || '').trim();
  const names: string[] = [];
  if (countryCode === 'VE') {
    names.push(...veCityNames(stateCode, countyCode));
  }
  for (const row of MAJOR_CITIES[countryCode] || []) {
    if (stateCode && stateCode !== OFAC_OTHER_VALUE && row.state !== stateCode) {
      continue;
    }
    if (
      countryCode !== 'US'
      && countyCode
      && countyCode !== OFAC_OTHER_VALUE
      && row.county
      && row.county !== countyCode
    ) {
      continue;
    }
    names.push(row.name);
  }
  return uniqueCityOptions(names);
}

function normalizeCityLookupKey(value: string): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\s+(city|town|village|cdp|borough)$/i, '');
}

function veCountyForCity(stateCode: string, cityName: string): string {
  const needle = normalizeCityLookupKey(cityName);
  if (!needle) return '';
  const counties = stateCode && stateCode !== OFAC_OTHER_VALUE
    ? (VE_GEO.counties[stateCode] || [])
    : Object.values(VE_GEO.counties).flat();
  for (const row of counties) {
    const names = VE_CITIES[row.code] || [];
    if (names.some((name) => normalizeCityLookupKey(name) === needle)) {
      return row.code;
    }
  }
  return '';
}

/** Suggested county/municipality for a typed or catalog city. Empty when unknown. */
export function ofacCountyFromCity(country: string, state: string, city: string): string {
  const countryCode = String(country || '').trim().toUpperCase();
  const stateCode = String(state || '').trim();
  const cityName = String(city || '').trim();
  if (!countryCode || !cityName || cityName === OFAC_OTHER_VALUE) {
    return '';
  }
  const needle = normalizeCityLookupKey(cityName);
  for (const row of MAJOR_CITIES[countryCode] || []) {
    if (stateCode && stateCode !== OFAC_OTHER_VALUE && row.state !== stateCode) {
      continue;
    }
    if (row.county && normalizeCityLookupKey(row.name) === needle) {
      return row.county;
    }
  }
  if (countryCode === 'US') {
    if (!stateCode || stateCode === OFAC_OTHER_VALUE) {
      return '';
    }
    return US_PLACES[stateCode]?.[needle] || '';
  }
  if (countryCode === 'VE') {
    return veCountyForCity(stateCode, cityName);
  }
  return '';
}

/** US types city first so county can auto-fill; VE/MX keep municipality before city. */
export function ofacCityBeforeCounty(country: string): boolean {
  return String(country || '').trim().toUpperCase() === 'US';
}

export function ofacAdminLabels(country: string, language: OfacLanguage = 'en'): {
  region: string;
  county: string;
  city: string;
} {
  const code = String(country || '').trim().toUpperCase();
  if (language === 'es') {
    if (code === 'US') {
      return { region: 'Estado', county: 'Condado', city: 'Ciudad' };
    }
    if (code === 'VE') {
      return { region: 'Estado', county: 'Municipio', city: 'Ciudad' };
    }
    if (code === 'MX') {
      return { region: 'Estado', county: 'Municipio', city: 'Ciudad' };
    }
    if (code === 'CO' || code === 'PE') {
      return { region: 'Departamento', county: 'Municipio', city: 'Ciudad' };
    }
    if (code === 'ES') {
      return { region: 'Comunidad', county: 'Provincia', city: 'Ciudad' };
    }
    if (code === 'BR') {
      return { region: 'Estado', county: 'Municipio', city: 'Ciudad' };
    }
    if (code === 'CA') {
      return { region: 'Provincia', county: 'Condado', city: 'Ciudad' };
    }
    return { region: 'Estado / región', county: 'Condado / municipio', city: 'Ciudad' };
  }
  if (code === 'US') {
    return { region: 'State', county: 'County', city: 'City' };
  }
  if (code === 'VE' || code === 'MX') {
    return { region: 'State', county: 'Municipality', city: 'City' };
  }
  if (code === 'CO' || code === 'PE') {
    return { region: 'Department', county: 'Municipality', city: 'City' };
  }
  if (code === 'ES') {
    return { region: 'Community', county: 'Province', city: 'City' };
  }
  if (code === 'BR') {
    return { region: 'State', county: 'Municipality', city: 'City' };
  }
  if (code === 'CA') {
    return { region: 'Province', county: 'County', city: 'City' };
  }
  return { region: 'State / region', county: 'County', city: 'City' };
}

export function ofacAddressLayout(country: string): OfacAddressField[] {
  const code = String(country || '').trim().toUpperCase();
  return ADDRESS_LAYOUTS[code] || GENERIC_ADDRESS_LAYOUT;
}

export function ofacAddressFieldLabel(
  field: OfacAddressField,
  country: string,
  language: OfacLanguage = 'en',
): string {
  const code = String(country || '').trim().toUpperCase();
  const admin = ofacAdminLabels(code, language);
  if (field === 'country') {
    return language === 'es' ? 'País' : 'Country';
  }
  if (field === 'line') {
    return language === 'es' ? 'Calle' : 'Street';
  }
  if (field === 'line2') {
    return language === 'es' ? 'Apto / unidad' : 'Apt / suite';
  }
  if (field === 'neighborhood') {
    if (code === 'VE') {
      return language === 'es' ? 'Urbanización' : 'Urbanization';
    }
    if (code === 'MX') {
      return language === 'es' ? 'Colonia' : 'Colonia';
    }
    return language === 'es' ? 'Barrio' : 'Neighborhood';
  }
  if (field === 'city') {
    return admin.city;
  }
  if (field === 'region') {
    return admin.region;
  }
  if (field === 'county') {
    return admin.county;
  }
  if (code === 'US') {
    return language === 'es' ? 'Código ZIP' : 'ZIP code';
  }
  return language === 'es' ? 'Código postal' : 'Postal code';
}

export function ofacNationalIdTypes(country: string): NationalIdType[] {
  const code = String(country || '').trim().toUpperCase();
  return NATIONAL_ID_TYPES[code] || GENERIC_NATIONAL_ID_TYPES;
}

const HISPANIC_NAME_COUNTRIES = new Set([
  'VE', 'MX', 'CO', 'ES', 'AR', 'CL', 'PE', 'EC', 'BO', 'PY', 'UY',
  'CR', 'PA', 'GT', 'HN', 'NI', 'SV', 'CU', 'DO', 'PR',
]);
const PORTUGUESE_NAME_COUNTRIES = new Set(['BR', 'PT']);

/** CLDR Person Names: Latin American countries file under the paternal surname. */
export function ofacDefaultNameStyle(country: string): OfacNameStyle {
  const code = String(country || '').trim().toUpperCase();
  if (PORTUGUESE_NAME_COUNTRIES.has(code)) return 'portuguese';
  if (HISPANIC_NAME_COUNTRIES.has(code)) return 'hispanic';
  return 'western';
}

const OFAC_SURNAME_PARTICLES = new Set(['de', 'del', 'la', 'las', 'los', 'y', 'da', 'do', 'dos', 'das']);

export function ofacCollapseNameTokens(fullName: string): string[] {
  const raw = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter((token) => {
      const normalized = token.toLowerCase().replace(/\./g, '');
      return Boolean(normalized) && !['jr', 'sr', 'ii', 'iii', 'iv'].includes(normalized);
    });
  const out: string[] = [];
  let index = 0;
  while (index < raw.length) {
    if (OFAC_SURNAME_PARTICLES.has(raw[index].toLowerCase()) && index + 1 < raw.length) {
      let compound = raw[index];
      while (index + 1 < raw.length && OFAC_SURNAME_PARTICLES.has(raw[index + 1].toLowerCase())) {
        index += 1;
        compound += ` ${raw[index]}`;
      }
      if (index + 1 < raw.length) {
        index += 1;
        compound += ` ${raw[index]}`;
      }
      out.push(compound);
      index += 1;
      continue;
    }
    out.push(raw[index]);
    index += 1;
  }
  return out;
}

function ofacSurnameCore(token: string): string {
  return String(token || '')
    .split(/\s+/)
    .filter((part) => part && !OFAC_SURNAME_PARTICLES.has(part.toLowerCase()))
    .join('');
}

/** Four given+family tokens whose last two look like family names (matches gateway hispanic parse). */
export function ofacLooksLikeTwoFamilyNames(fullName: string): boolean {
  const tokens = ofacCollapseNameTokens(fullName);
  if (tokens.length < 4) return false;
  return ofacSurnameCore(tokens[tokens.length - 2]).length >= 4
    && ofacSurnameCore(tokens[tokens.length - 1]).length >= 4;
}

export type OfacNameDetectHints = {
  nationalities?: string[];
  searchCountry?: string;
  surname2?: string;
};

/**
 * Infer Western vs Spanish vs Portuguese from the written name.
 * Four-token two-surname names become Spanish (or Portuguese when nationality is BR/PT).
 * Three-token names stay Western so "Stefany Fabiola Goss" is not split as two surnames.
 * Empty names fall back to nationality / search country.
 */
export function ofacDetectNameStyle(
  fullName: string,
  hints: OfacNameDetectHints = {},
): OfacNameStyle {
  const codes = (hints.nationalities || [])
    .map((row) => String(row || '').trim().toUpperCase())
    .filter(Boolean);
  const search = String(hints.searchCountry || '').trim().toUpperCase();
  const origin = codes.find((code) => code !== 'US' && code !== 'USA') || '';
  const countryPrior = ofacDefaultNameStyle(origin || search);
  const written = String(fullName || '').trim();
  const twoFamily = ofacLooksLikeTwoFamilyNames(written) || Boolean(String(hints.surname2 || '').trim());
  if (twoFamily) {
    return countryPrior === 'portuguese' ? 'portuguese' : 'hispanic';
  }
  if (!written) return countryPrior;
  return 'western';
}

export function parseOfacLegalName(
  fullName: string,
  style?: OfacNameStyle | string | null,
): { givenName: string; givenName2: string; surname: string; surname2: string } {
  const tokens = ofacCollapseNameTokens(fullName);
  const order = style === 'portuguese' ? 'portuguese' : style === 'western' ? 'western' : 'hispanic';
  if (!tokens.length) {
    return { givenName: '', givenName2: '', surname: '', surname2: '' };
  }
  if (tokens.length === 1) {
    return { givenName: tokens[0], givenName2: '', surname: '', surname2: '' };
  }
  if (order === 'western' || tokens.length === 2) {
    return {
      givenName: tokens[0],
      givenName2: tokens.slice(1, -1).join(' '),
      surname: tokens[tokens.length - 1],
      surname2: '',
    };
  }
  const surnameA = tokens[tokens.length - 2];
  const surnameB = tokens[tokens.length - 1];
  return {
    givenName: tokens[0],
    givenName2: tokens.length >= 4 ? tokens.slice(1, -2).join(' ') : '',
    surname: order === 'portuguese' ? surnameB : surnameA,
    surname2: order === 'portuguese' ? surnameA : surnameB,
  };
}

export type OfacNameOrderFields = {
  nameStyle: OfacNameStyle;
  givenName: string;
  givenName2: string;
  surname: string;
  surname2: string;
  entityName: string;
};

function ofacComposeFromNameOrder(
  fields: Pick<OfacNameOrderFields, 'givenName' | 'givenName2' | 'surname' | 'surname2'>,
  style: OfacNameStyle | string,
): string {
  return composeOfacLegalName({
    style,
    givenName: fields.givenName,
    givenName2: fields.givenName2,
    surname: fields.surname,
    surname2: fields.surname2,
  });
}

export function applyOfacNameOrderEdit(
  previous: OfacNameOrderFields,
  next: OfacNameOrderFields,
  options: { detect?: boolean; locked?: boolean; hints?: OfacNameDetectHints } = {},
): { value: OfacNameOrderFields; locked: boolean } {
  const styleChanged = next.nameStyle !== previous.nameStyle;
  const partsChanged = next.givenName !== previous.givenName
    || next.givenName2 !== previous.givenName2
    || next.surname !== previous.surname
    || next.surname2 !== previous.surname2;
  const composedChanged = next.entityName !== previous.entityName && !partsChanged;
  if (styleChanged) {
    return {
      value: {
        ...next,
        entityName: ofacComposeFromNameOrder(next, next.nameStyle) || next.entityName,
      },
      locked: true,
    };
  }
  let style = next.nameStyle;
  const written = (composedChanged ? next.entityName : ofacComposeFromNameOrder(next, next.nameStyle) || next.entityName).trim();
  // Part edits (including a middle name) must not reclassify the name. A fourth
  // token would otherwise flip Western to Spanish and remount the inputs.
  if (options.detect && !options.locked && !partsChanged) {
    style = ofacDetectNameStyle(written, {
      ...options.hints,
      surname2: composedChanged ? '' : next.surname2,
    });
  }
  if (composedChanged) {
    const parsed = parseOfacLegalName(next.entityName, style);
    return {
      value: {
        ...parsed,
        nameStyle: style,
        entityName: String(next.entityName || '').trim() || ofacComposeFromNameOrder(parsed, style),
      },
      locked: Boolean(options.locked),
    };
  }
  return {
    value: {
      ...next,
      nameStyle: style,
      entityName: ofacComposeFromNameOrder(next, style) || next.entityName,
    },
    locked: Boolean(options.locked),
  };
}

export function ofacUsesUsPeopleDirectories(country: string): boolean {
  const code = String(country || '').trim().toUpperCase();
  return !code || code === 'US' || code === 'USA';
}

export function ofacLegalNameSearchVariants(
  fullName: string,
  style?: OfacNameStyle | string | null,
): string[] {
  const tokens = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 3) return [];
  const order = style === 'portuguese' ? 'portuguese' : style === 'western' ? 'western' : 'hispanic';
  if (order === 'western') return [];
  const first = tokens[0];
  const middle = tokens.length >= 4 ? tokens.slice(1, -2).join(' ') : '';
  const surnameA = tokens[tokens.length - 2];
  const surnameB = tokens[tokens.length - 1];
  const paternal = order === 'portuguese' ? surnameB : surnameA;
  const maternal = order === 'portuguese' ? surnameA : surnameB;
  const rows = [
    [first, middle, paternal, maternal].filter(Boolean).join(' '),
    [first, middle, paternal].filter(Boolean).join(' '),
    [first, paternal, maternal].filter(Boolean).join(' '),
    [first, paternal].filter(Boolean).join(' '),
  ];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const row of rows) {
    const key = row.toLowerCase();
    if (!row || seen.has(key)) continue;
    seen.add(key);
    out.push(row);
  }
  return out;
}

export function ofacNamePartLabels(
  style: OfacNameStyle | string | undefined,
  language: OfacLanguage = 'en',
): {
  givenName: string;
  givenName2: string;
  surname: string;
  surname2: string;
} {
  const order = style === 'portuguese' ? 'portuguese' : style === 'western' ? 'western' : 'hispanic';
  if (language === 'es') {
    if (order === 'portuguese') {
      return {
        givenName: 'Primer nombre',
        givenName2: 'Segundo nombre',
        surname: 'Apellido paterno (último)',
        surname2: 'Apellido materno (primero)',
      };
    }
    if (order === 'western') {
      return {
        givenName: 'Nombre',
        givenName2: 'Segundo nombre',
        surname: 'Apellido',
        surname2: 'Segundo apellido',
      };
    }
    return {
      givenName: 'Primer nombre',
      givenName2: 'Segundo nombre',
      surname: 'Apellido paterno',
      surname2: 'Apellido materno',
    };
  }
  if (order === 'portuguese') {
    return {
      givenName: 'Given name',
      givenName2: 'Additional given name',
      surname: 'Paternal surname (last)',
      surname2: 'Maternal surname (first)',
    };
  }
  if (order === 'western') {
    return {
      givenName: 'First name',
      givenName2: 'Middle name',
      surname: 'Last name',
      surname2: 'Second last name',
    };
  }
  return {
    givenName: 'Given name',
    givenName2: 'Additional given name',
    surname: 'Paternal surname',
    surname2: 'Maternal surname',
  };
}

export function ofacNamePartHelp(
  style: OfacNameStyle | string | undefined,
  part: OfacNamePartKey,
  language: OfacLanguage = 'en',
): string {
  const order = style === 'portuguese' ? 'portuguese' : style === 'western' ? 'western' : 'hispanic';
  const copy = {
    en: {
      givenName: 'The first personal name. Not a surname. Example: María, José, Michael.',
      givenName2: 'A second given name or middle name. Not a family name. Leave blank if they only have one given name.',
      hispanicSurname: 'Father’s family name. Written first of the two surnames. Most Spanish records and SDN file under this name.',
      hispanicSurname2: 'Mother’s family name. Written after the paternal surname. Keep it even if they go by the paternal name in daily use.',
      portugueseSurname: 'Father’s family name. Written last. This is the primary surname for filing and short reference in Portuguese/Brazilian usage.',
      portugueseSurname2: 'Mother’s family name. Written first of the two surnames, before the father’s. Do not put this in the paternal field.',
      westernSurname: 'The family name. Hyphenated last names stay in this one field (for example Smith-Jones).',
      westernSurname2: 'Optional. Use only if they actually have a second separate family name. Leave blank for a typical U.S. or UK name.',
    },
    es: {
      givenName: 'El primer nombre de pila. No es un apellido. Ejemplo: María, José, Michael.',
      givenName2: 'Segundo nombre de pila. No es un apellido. Déjalo vacío si solo tienen un nombre.',
      hispanicSurname: 'Apellido del padre. Se escribe primero de los dos. SDN y la mayoría de registros hispanos se archivan por este apellido.',
      hispanicSurname2: 'Apellido de la madre. Se escribe después del paterno. Consérvalo aunque en el día a día usen solo el paterno.',
      portugueseSurname: 'Apellido del padre. Se escribe al final. Es el apellido principal para archivar en uso portugués/brasileño.',
      portugueseSurname2: 'Apellido de la madre. Se escribe primero de los dos, antes del del padre. No lo pongas en el campo paterno.',
      westernSurname: 'El apellido de familia. Un apellido con guion queda en este mismo campo (por ejemplo Smith-Jones).',
      westernSurname2: 'Opcional. Úsalo solo si realmente tienen un segundo apellido distinto. Déjalo vacío en un nombre típico de EE.UU. o Reino Unido.',
    },
  }[language];
  if (part === 'givenName') {
    return copy.givenName;
  }
  if (part === 'givenName2') {
    return copy.givenName2;
  }
  if (order === 'portuguese') {
    return part === 'surname' ? copy.portugueseSurname : copy.portugueseSurname2;
  }
  if (order === 'western') {
    return part === 'surname' ? copy.westernSurname : copy.westernSurname2;
  }
  return part === 'surname' ? copy.hispanicSurname : copy.hispanicSurname2;
}

export function ofacComposedLegalNameHelp(language: OfacLanguage = 'en'): string {
  return language === 'es'
    ? 'Se arma solo a partir de las partes. Es el nombre que SDN compara. Edítalo solo si el documento legal usa otra grafía o un “y” / “de”.'
    : 'Filled from the parts above. This is the name SDN matches. Edit it only if the legal document uses a different spelling or a particle such as “y” or “de”.';
}

export function resolvedOfacPart(value?: string | null, other?: string | null): string {
  const selected = String(value || '').trim();
  const typed = String(other || '').trim();
  if (!selected || selected === OFAC_OTHER_VALUE) {
    return typed;
  }
  return selected;
}

export function withOtherOption(
  options: OfacSelectOption[],
  language: OfacLanguage = 'en',
): OfacSelectOption[] {
  return [
    ...options,
    {
      value: OFAC_OTHER_VALUE,
      label: language === 'es' ? 'Otro (escribir)' : 'Other (type it)',
    },
  ];
}

function lookupStateLabel(country: string, state: string): string {
  const raw = String(state || '').trim();
  if (!raw) {
    return '';
  }
  const match = ofacStateOptions(country).find((row) => row.value === raw || row.label === raw);
  return match?.label || raw;
}

function lookupCountyLabel(country: string, state: string, county: string): string {
  const raw = String(county || '').trim();
  if (!raw) {
    return '';
  }
  const match = ofacCountyOptions(country, state).find((row) => row.value === raw || row.label === raw);
  return match?.label || raw;
}

export function composeOfacLegalName(input: {
  style?: OfacNameStyle | string | null;
  givenName?: string | null;
  givenName2?: string | null;
  surname?: string | null;
  surname2?: string | null;
}): string {
  const given = String(input.givenName || '').trim();
  const given2 = String(input.givenName2 || '').trim();
  const surname = String(input.surname || '').trim();
  const surname2 = String(input.surname2 || '').trim();
  const style = input.style === 'portuguese' ? 'portuguese' : input.style === 'western' ? 'western' : 'hispanic';
  const surnames = style === 'portuguese'
    ? [surname2, surname]
    : [surname, surname2];
  return [given, given2, ...surnames].filter(Boolean).join(' ');
}

export function composeOfacPlace(input: {
  country?: string | null;
  otherCountry?: string | null;
  region?: string | null;
  otherRegion?: string | null;
  county?: string | null;
  otherCounty?: string | null;
  city?: string | null;
  otherCity?: string | null;
  language?: OfacLanguage;
}): string {
  const language = input.language || 'en';
  const countryCode = resolvedOfacPart(input.country, input.otherCountry);
  const regionRaw = resolvedOfacPart(input.region, input.otherRegion);
  const countyRaw = resolvedOfacPart(input.county, input.otherCounty);
  const city = resolvedOfacPart(input.city, input.otherCity);
  const lookupCountry = String(input.country || '').trim() === OFAC_OTHER_VALUE
    ? ''
    : String(input.country || '').trim();
  const lookupRegion = String(input.region || '').trim() === OFAC_OTHER_VALUE
    ? ''
    : String(input.region || '').trim();
  const region = lookupStateLabel(lookupCountry, regionRaw) || regionRaw;
  const county = lookupCountyLabel(lookupCountry, lookupRegion, countyRaw) || countyRaw;
  const country = ofacCountryLabel(countryCode, language) || countryCode;
  return [city, county, region, country].filter(Boolean).join(', ');
}
